import React, { useEffect, useState } from "react"
import { Table, Button, Tag, Spin, Segmented, DatePicker, Checkbox } from "antd"
import { DownloadOutlined } from "@ant-design/icons"
import Axios from "axios";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
const { RangePicker } = DatePicker;

interface ConnectionLog {
    connectionLogId: string;
    orgaId: string;
    orgaName: string;
    collEmail: string;
    softwareName: 'Multiscan' | 'Scan&Conquest Négoce' | 'Conquest' | 'Accueil';
    logDate: Date;
    platform: 'WEB' | 'WEB_REFRESH' | 'MOBILE_APP' | 'MOBILE_APP_REFRESH' | 'AUTO_GENERATED';
    lastActionLogDate?: Date;
}

interface ConnectionLogsPerUser {
    orgaId: string;
    orgaName: string;
    collEmail: string;
    softwarePlatformCombination: string;
    date: Date;
    connectionCount: number;
}

interface SoftwarePlatformCombination {
    softwareName: ConnectionLog['softwareName'][];
    platform: ConnectionLog['platform'][];
}

const softwareNameTagsColors: { [key in ConnectionLog['softwareName']]: string } = {
    'Multiscan': 'green',
    'Scan&Conquest Négoce': 'blue',
    'Conquest': 'orange',
    'Accueil': 'default',
};

const platformTagsColors: { [key in ConnectionLog['platform']]: string } = {
    'WEB': 'purple',
    'WEB_REFRESH': 'magenta',
    'MOBILE_APP': 'lime',
    'MOBILE_APP_REFRESH': 'cyan',
    'AUTO_GENERATED': 'red',
};

const softwarePlatformCombinationTagsColors: { [key: string]: string } = {
    'WEB_Multiscan': 'green',
    'WEB_S&C': 'blue',
    'WEB_Conquest': 'orange',
    'WEB_Accueil': 'default',
    'APP_S&C': 'lime',
};

const timeViews = ["Personnalisé", "Aujourd'hui", "Hier", "Cette semaine", "Ce mois", "Tous"];

const tableViews = ["Vue log", "Vue utilisateur"];

const softwarePlatformCombinations: { [key: string]: SoftwarePlatformCombination } = {
    'WEB_Multiscan': {
        softwareName: ['Multiscan'],
        platform: ['WEB', 'WEB_REFRESH', 'AUTO_GENERATED'],
    },
    'WEB_S&C': {
        softwareName: ['Scan&Conquest Négoce'],
        platform: ['WEB', 'WEB_REFRESH', 'AUTO_GENERATED'],
    },
    'WEB_Conquest': {
        softwareName: ['Conquest'],
        platform: ['WEB', 'WEB_REFRESH', 'AUTO_GENERATED']
    },
    'WEB_Accueil': {
        softwareName: ['Accueil'],
        platform: ['WEB', 'WEB_REFRESH', 'AUTO_GENERATED']
    },
    'APP_S&C': {
        softwareName: ['Scan&Conquest Négoce'],
        platform: ['MOBILE_APP', 'MOBILE_APP_REFRESH']
    },
};

export function ConnectionLogs() {
    const [loading, setLoading] = useState<boolean>(false);

    const [tableView, setTableView] = useState<typeof tableViews[number]>("Vue log");

    const [timeView, setTimeView] = useState<typeof timeViews[number]>("Aujourd'hui");
    const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
    const [customEndDate, setCustomEndDate] = useState<Date | null>(null);

    const [logs, setLogs] = useState<ConnectionLog[]>([]);
    const [uniqueEmails, setUniqueEmails] = useState<string[]>([])
    const [uniqueSoftwares, setUniqueSoftwares] = useState<string[]>([])
    const [uniquePlatforms, setUniquePlatforms] = useState<string[]>([])
    const [uniqueOrgaNames, setUniqueOrgaNames] = useState<string[]>([])

    const [displayAdminUsers, setDisplayAdminUsers] = useState<boolean>(true);

    const [activeFilters, setActiveFilters] = useState({
        orgaNames: [] as string[],
        emails: [] as string[],
        softwares: [] as string[],
        platforms: [] as string[],
        combinations: [] as string[]
    });
    const [showZeroCounts, setShowZeroCounts] = useState<boolean>(true);

    // *********************** Table Columns ***********************
    const logsColumns: ColumnsType<ConnectionLog> = [
        {
            title: 'Organisation',
            dataIndex: 'orgaName',
            key: 'orgaName',
            width: '16.6%',
            filterSearch: true,
            filters: uniqueOrgaNames.map(orgaName => ({ text: orgaName, value: orgaName })),
            onFilter: (value, record) => record.orgaName === value,
            filteredValue: activeFilters.orgaNames,
            sorter: (a, b) => a.orgaName.localeCompare(b.orgaName),
            // Display orga name as title and orga ID as subtitle
            render: (orgaName: ConnectionLog['orgaName'], record: ConnectionLog) => (
                <div>
                    <div>{orgaName}</div>
                    <div style={{ fontSize: '0.8em', color: 'gray' }}>{record.orgaId}</div>
                </div>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'collEmail',
            key: 'collEmail',
            width: '16.6%',
            filterSearch: true,
            filters: uniqueEmails.map(email => ({ text: email, value: email })),
            onFilter: (value, record) => record.collEmail === value,
            filteredValue: activeFilters.emails,
            sorter: (a, b) => a.collEmail.localeCompare(b.collEmail),
        },
        {
            title: 'Plateforme',
            dataIndex: 'platform',
            key: 'platform',
            width: '16.6%',
            filterSearch: true,
            filters: uniquePlatforms.map(platform => ({ text: platform, value: platform })),
            onFilter: (value, record) => record.platform === value,
            filteredValue: activeFilters.platforms,
            render: (platform: ConnectionLog['platform']) => {
                const color = platformTagsColors[platform];
                return (
                    <Tag color={color} key={platform}>
                        {platform}
                    </Tag>
                );
            },
            sorter: (a, b) => a.platform.localeCompare(b.platform),
        },
        {
            title: 'Outil',
            dataIndex: 'softwareName',
            key: 'softwareName',
            width: '16.6%',
            filterSearch: true,
            filters: uniqueSoftwares.map(software => ({ text: software, value: software })),
            onFilter: (value, record) => record.softwareName === value,
            filteredValue: activeFilters.softwares,
            render: (softwareName: ConnectionLog['softwareName']) => {
                const color = softwareNameTagsColors[softwareName];
                return (
                    <Tag color={color} key={softwareName}>
                        {softwareName}
                    </Tag>
                );
            },
            sorter: (a, b) => a.softwareName.localeCompare(b.softwareName),
        },
        {
            title: 'Date de connexion',
            dataIndex: 'logDate',
            key: 'logDate',
            width: '16.6%',
            sorter: (a, b) => a.logDate.getTime() - b.logDate.getTime(),
            render: (date: Date) => date.toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }),
        },
        {
            title: 'Dernière action',
            dataIndex: 'lastActionLogDate',
            key: 'lastActionLogDate',
            width: '16.6%',
            sorter: (a, b) => {
                const dateA = a.lastActionLogDate ? a.lastActionLogDate.getTime() : 0;
                const dateB = b.lastActionLogDate ? b.lastActionLogDate.getTime() : 0;
                return dateA - dateB;
            },
            render: (date?: Date) => date ? date.toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }) : 'N/A',
        },
    ];

    const logsPerUserColumns: ColumnsType<ConnectionLogsPerUser> = [
        {
            title: 'Organisation',
            dataIndex: 'orgaName',
            key: 'orgaName',
            width: '20%',
            filterSearch: true,
            filters: uniqueOrgaNames.map(orgaName => ({ text: orgaName, value: orgaName })),
            onFilter: (value, record) => record.orgaName === value,
            filteredValue: activeFilters.orgaNames,
            sorter: (a, b) => a.orgaName.localeCompare(b.orgaName),
            render: (orgaName: ConnectionLogsPerUser['orgaName'], record: ConnectionLogsPerUser) => (
                <div>
                    <div>{orgaName}</div>
                    <div style={{ fontSize: '0.8em', color: 'gray' }}>{record.orgaId}</div>
                </div>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'collEmail',
            key: 'collEmail',
            width: '20%',
            filterSearch: true,
            filters: uniqueEmails.map(email => ({ text: email, value: email })),
            onFilter: (value, record) => record.collEmail === value,
            filteredValue: activeFilters.emails,
            sorter: (a, b) => a.collEmail.localeCompare(b.collEmail),
        },
        {
            title: 'Type d\'événement',
            dataIndex: 'softwarePlatformCombination',
            key: 'softwarePlatformCombination',
            width: '20%',
            filterSearch: true,
            filters: Object.keys(softwarePlatformCombinations).map(key => ({ text: key, value: key })),
            onFilter: (value, record) => record.softwarePlatformCombination === value,
            filteredValue: activeFilters.combinations,
            sorter: (a, b) => a.softwarePlatformCombination.localeCompare(b.softwarePlatformCombination),
            render: (combination: string) => {
                const config = softwarePlatformCombinations[combination];
                return (
                    <div>
                        <Tag color={softwarePlatformCombinationTagsColors[combination] || 'default'} key={combination}>
                            {combination}
                        </Tag>
                        <div style={{ fontSize: '0.8em', color: 'gray' }}>
                            {config.softwareName.join(', ')} - {config.platform.join(', ')}
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: '20%',
            sorter: (a, b) => a.date.getTime() - b.date.getTime(),
            render: (date: Date) => date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            }),
        },
        {
            title: 'Nombre de connexions',
            dataIndex: 'connectionCount',
            key: 'connectionCount',
            width: '20%',
            sorter: (a, b) => a.connectionCount - b.connectionCount,
            render: (count: number) => (
                <span style={{ fontWeight: 'bold' }}>{count}</span>
            ),
        }
    ];

    // *********************** Apply all filters to data ***********************
    const applyTableFilters = (data: ConnectionLog[]) => {
        return data.filter(log => {
            // Apply orga filter
            if (activeFilters.orgaNames.length > 0 && !activeFilters.orgaNames.includes(log.orgaName)) {
                return false;
            }
            // Apply email filter
            if (activeFilters.emails.length > 0 && !activeFilters.emails.includes(log.collEmail)) {
                return false;
            }
            // Apply software filter
            if (activeFilters.softwares.length > 0 && !activeFilters.softwares.includes(log.softwareName)) {
                return false;
            }
            // Apply platform filter
            if (activeFilters.platforms.length > 0 && !activeFilters.platforms.includes(log.platform)) {
                return false;
            }
            return true;
        });
    };

    const applyUserTableFilters = (data: ConnectionLogsPerUser[]) => {
        return data.filter(log => {
            // Apply orga filter
            if (activeFilters.orgaNames.length > 0 && !activeFilters.orgaNames.includes(log.orgaName)) {
                return false;
            }
            // Apply email filter
            if (activeFilters.emails.length > 0 && !activeFilters.emails.includes(log.collEmail)) {
                return false;
            }
            // Apply combination filter
            if (activeFilters.combinations.length > 0 && !activeFilters.combinations.includes(log.softwarePlatformCombination)) {
                return false;
            }
            return true;
        });
    };

    // *********************** Filtered Logs ***********************
    const filteredLogs = React.useMemo(() => {
        // Server already filtered by time and admin users, now only apply table filters
        return applyTableFilters(logs);
    }, [logs, activeFilters]);

    // *********************** Aggregate Data Per User ***********************
    const aggregatedLogsPerUserAndDate = React.useMemo(() => {
        if (tableView !== "Vue utilisateur") return [];

        const result: ConnectionLogsPerUser[] = [];

        // Get all unique dates in the filtered logs
        const minDate = filteredLogs.reduce((min, log) => log.logDate < min ? log.logDate : min, new Date());
        const maxDate = filteredLogs.reduce((max, log) => log.logDate > max ? log.logDate : max, new Date(0));

        const dateSet = new Set<string>();
        for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
            dateSet.add(new Date(d).toDateString());
        }

        const uniqueDates = Array.from(dateSet).sort();

        // Get all unique emails
        const uniqueEmailsInFiltered = [...new Set(filteredLogs.map(log => log.collEmail))];

        uniqueEmailsInFiltered.forEach(email => {
            const userLogs = filteredLogs.filter(log => log.collEmail === email);
            const userInfo = userLogs[0]; // Get org info from first log

            // For each software-platform combination and each date, count connections for the user
            Object.keys(softwarePlatformCombinations).forEach(combinationKey => {
                const combination = softwarePlatformCombinations[combinationKey];
                uniqueDates.forEach(dateString => {
                    const logsForDate = userLogs.filter(log =>
                        log.logDate.toDateString() === dateString
                    );
                    const count = logsForDate.filter(log =>
                        combination.softwareName.includes(log.softwareName) &&
                        combination.platform.includes(log.platform)
                    ).length;

                    if (count > 0) {
                        result.push({
                            orgaId: userInfo.orgaId,
                            orgaName: userInfo.orgaName,
                            collEmail: email,
                            softwarePlatformCombination: combinationKey,
                            date: new Date(dateString),
                            connectionCount: count
                        });
                    }
                });
            });
        });

        // Apply user table filters
        const filteredResult = applyUserTableFilters(result);

        // Sort by email first, then by date, then by combination
        return filteredResult.sort((a, b) => {
            if (a.collEmail !== b.collEmail) {
                return a.collEmail.localeCompare(b.collEmail);
            }
            if (a.softwarePlatformCombination !== b.softwarePlatformCombination) {
                return a.softwarePlatformCombination.localeCompare(b.softwarePlatformCombination);
            }
            return a.date.getTime() - b.date.getTime();
        });
    }, [filteredLogs, tableView, activeFilters]);

    const handleTableChange = (pagination: any, filters: any) => {
        setActiveFilters({
            orgaNames: filters.orgaName || [],
            emails: filters.collEmail || [],
            softwares: filters.softwareName || [],
            platforms: filters.platform || [],
            combinations: filters.softwarePlatformCombination || []
        });
    };

    // *********************** Effects ***********************
    useEffect(() => {
        // Don't fetch if "Personnalisé" is selected but dates are not set
        if (timeView === "Personnalisé" && !customStartDate && !customEndDate) {
            setLogs([]);
            setUniqueEmails([]);
            setUniqueSoftwares([]);
            setUniquePlatforms([]);
            setUniqueOrgaNames([]);
            return;
        }

        setLoading(true);
        const params = new URLSearchParams({
            timeView,
            displayAdminUsers: displayAdminUsers.toString(),
            ...(customStartDate && { customStartDate: customStartDate.toISOString() }),
            ...(customEndDate && { customEndDate: customEndDate.toISOString() }),
        });
        
        Axios.get(`/logs/getAllConnectionLogs?${params.toString()}`)
            .then(res => {
                const connectionLogsData = res.data.map((log: any) => ({
                    ...log,
                    logDate: new Date(log.logDate),
                    lastActionLogDate: log.lastActionLogDate !== "N/A" ? new Date(log.lastActionLogDate) : undefined,
                }));
                setLogs(connectionLogsData);
                // Extract unique values for filters
                setUniqueEmails([...new Set(connectionLogsData.map((e: any) => e.collEmail))] as string[]);
                setUniqueSoftwares([...new Set(connectionLogsData.map((e: any) => e.softwareName))] as string[]);
                setUniquePlatforms([...new Set(connectionLogsData.map((e: any) => e.platform))] as string[]);
                setUniqueOrgaNames([...new Set(connectionLogsData.map((e: any) => e.orgaName))] as string[]);
            })
            .catch(err => {
                if (Axios.isCancel(err)) return; // Ignore abort
                console.error(err);
            })
            .finally(() => setLoading(false));
    }, [timeView, customStartDate, customEndDate, displayAdminUsers]);

    useEffect(() => {
        // Reset custom dates when time view changes
        if (timeView !== "Personnalisé") {
            setCustomStartDate(null);
            setCustomEndDate(null);
        }
    }, [timeView]);

    // Add effect to clear inappropriate filters when switching views
    useEffect(() => {
        if (tableView === "Vue utilisateur") {
            // Clear software and platform filters as they don't apply to user view
            setActiveFilters(prev => ({
                ...prev,
                softwares: [],
                platforms: []
            }));
        } else {
            // Clear combination filters as they don't apply to log view
            setActiveFilters(prev => ({
                ...prev,
                combinations: []
            }));
        }
    }, [tableView]);

    useEffect(() => {
        // If all filters are empty, set showZeroCounts to false
        const allFiltersEmpty = Object.values(activeFilters).every(arr => arr.length === 0);
        if (allFiltersEmpty) {
            setShowZeroCounts(true);
        } else {
            setShowZeroCounts(false);
        }
    }, [activeFilters]);

    // *********************** Render ***********************
    const renderTagSummary = (
        data: { [key: string]: string },
        getCount: (key: string) => number
    ) => {
        return (
            <div style={{ display: 'flex', gap: '20px' }}>
                {Object.keys(data).map(key => {
                    const totalCount = getCount(key);
                    const color = data[key] || 'default';
                    // Skip rendering if count is 0 and showZeroCounts is false
                    if (totalCount === 0 && !showZeroCounts) {
                        return null;
                    }
                    return (
                        <div key={key} style={{ marginTop: '5px' }}>
                            <Tag color={color} key={key}>
                                {key}
                            </Tag>
                            : <strong>{totalCount}</strong> connexion{totalCount > 1 ? 's' : ''}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                    type="primary"
                    shape="round"
                    onClick={() => {
                        const params = new URLSearchParams({
                            tableView,
                            timeView,
                            customStartDate: customStartDate ? customStartDate.toISOString() : '',
                            customEndDate: customEndDate ? customEndDate.toISOString() : '',
                            displayAdminUsers: displayAdminUsers.toString(),
                        });
                        window.open(`/logs/getAllConnectionLogsCSV?${params.toString()}`, '_blank');
                    }}
                    icon={<DownloadOutlined />}
                    size="middle"
                >
                    Télécharger tous les logs de connexion (.csv)
                </Button>
                <Segmented
                    options={timeViews}
                    value={timeView}
                    onChange={setTimeView}
                    disabled={loading}
                />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <Segmented
                        options={tableViews}
                        value={tableView}
                        onChange={setTableView}
                        disabled={loading}
                    />
                    <Checkbox
                        checked={displayAdminUsers}
                        onChange={e => setDisplayAdminUsers(e.target.checked)}
                        disabled={loading}
                    >
                        Afficher les données des @admin.fr
                    </Checkbox>
                </div>
                {timeView === "Personnalisé" && (
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <RangePicker
                            value={
                                customStartDate && customEndDate
                                    ? [dayjs(customStartDate), dayjs(customEndDate)]
                                    : null
                            }
                            onChange={(dates) => {
                                if (dates && dates.length === 2 && dates[0] && dates[1]) {
                                    setCustomStartDate(dates[0].toDate());
                                    setCustomEndDate(dates[1].toDate());
                                } else {
                                    setCustomStartDate(null);
                                    setCustomEndDate(null);
                                }
                            }}
                        />
                    </div>
                )}
            </div>
            {tableView === "Vue utilisateur" &&
                renderTagSummary(
                    softwarePlatformCombinationTagsColors,
                    (key) =>
                        aggregatedLogsPerUserAndDate
                            .filter(log => log.softwarePlatformCombination === key)
                            .reduce((sum, log) => sum + log.connectionCount, 0)
                )
            }

            {tableView === "Vue log" &&
                <div style={{ display: 'ruby-text' }}>
                    {renderTagSummary(
                        softwareNameTagsColors,
                        (key) =>
                            filteredLogs.filter(log => log.softwareName === key).length
                    )}
                    <div style={{ width: '10px' }} />
                    {renderTagSummary(
                        platformTagsColors,
                        (key) =>
                            filteredLogs.filter(log => log.platform === key).length
                    )}
                </div>
            }
            <br />
            <br />
            {loading ?
                <div style={{ textAlign: 'center' }}>
                    <Spin size="large" tip="Chargement des données...">
                        <div style={{ padding: 50, background: 'rgba(0, 0, 0, 0.05)', borderRadius: 4 }} />
                    </Spin>
                </div>
                :
                tableView === "Vue utilisateur" ?
                    <Table
                        pagination={false}
                        columns={logsPerUserColumns}
                        dataSource={aggregatedLogsPerUserAndDate}
                        scroll={{ y: '100%' }}
                        rowKey={(record: ConnectionLogsPerUser, index: number) =>
                            record.date ?
                                `${record.collEmail}-${record.softwarePlatformCombination}-${record.date.toISOString()}` :
                                `${record.collEmail}-${record.softwarePlatformCombination}-${index}`
                        }
                        virtual
                        onChange={handleTableChange}
                    />
                    :
                    <Table
                        pagination={false}
                        columns={logsColumns}
                        dataSource={filteredLogs}
                        scroll={{ y: '100%' }}
                        rowKey="connectionLogId"
                        virtual
                        onChange={handleTableChange}
                    />
            }
        </>
    )
}